import type { GaiaBotMessage } from "@expert/shared-types";

import { type GaiaWebSocketResponse, gaiaWsEventBus } from "@expert/gaia";

import type { useInvokeExpertAssist } from "../../hooks/useSendExpertAssistMessage";

import { parseExpertAssistMessage } from "../../utils/handleIncomingMessage";
import { registerHighlightAssistRequest } from "./highlightAssistRequests";

export type HighlightAssistReply = { status: "loading" } | { status: "failed" } | { status: "answered"; text: string };

/** How a sent ask ended: answered, or why it wasn't (`closed` = the expert closed the menu first). */
export type HighlightAssistOutcome = "answered" | "empty" | "error" | "timeout" | "closed";

export interface HighlightAssistSettled {
    outcome: HighlightAssistOutcome;
    /** From the send to the outcome. */
    durationMs: number;
    /** The reply's own id and analytics (citations, intent), when one arrived. */
    replyMessageId?: string;
    replyAnalytics?: Record<string, unknown>;
}

const REPLY_TIMEOUT_MS = 20_000;

interface AskProps {
    invoke: ReturnType<typeof useInvokeExpertAssist>;
    text: string;
    onSent: (sent: { appName: string; experimentName?: string; messageId: string }) => void;
    onReply: (reply: HighlightAssistReply) => void;
    /** Once per ask that was sent, however it ended. An ask that never went out reports nothing. */
    onSettled: (settled: HighlightAssistSettled) => void;
}

/**
 * Asks Assist about highlighted text out-of-band: nothing is added to the timeline, and the reply is matched by
 * correlationId and handed to `onReply` instead of rendering as a bubble. Returns a cleanup that stops listening;
 * an ask still waiting then settles as `closed`.
 *
 * Accepted risk, conversation-memory bleed: the ask uses the timeline's Assist session (`sessionId` /
 * `sessionGroupId`), so Assist's later answers in the timeline may draw on highlight questions the expert can't
 * see there. Dead-air asks carry the same risk (see `apps/expert-ui/src/dead-air/README.md`).
 */
export function askAssistAboutHighlight({ invoke, text, onSent, onReply, onSettled }: AskProps): () => void {
    const messageId = crypto.randomUUID();
    const correlationId = crypto.randomUUID();
    registerHighlightAssistRequest(correlationId);

    let isDone = false;
    // Set once the ask is sent and listening; closing calls it to stop and report `closed`.
    let stop: (() => void) | undefined;

    // Highlighted text is often the customer's own words, so it stays out of the logs.
    const question = `Tell me more about "${text}"`;
    void invoke({ text: question, messageId, correlationId, isTextSensitive: true }).then((result) => {
        if (!result.isSent) {
            if (!isDone) onReply({ status: "failed" });
            isDone = true;
            return;
        }

        // Listening only now is safe: the reply comes on a later websocket event, after this continuation runs.
        const { sentAt } = result;
        onSent({ appName: result.appName, experimentName: result.experimentName, messageId });
        const settle = (settled: Omit<HighlightAssistSettled, "durationMs">) =>
            onSettled({ ...settled, durationMs: Math.round(performance.now() - sentAt) });

        // Closed while the send was in flight: the ask still went out, so report it as closed.
        if (isDone) {
            settle({ outcome: "closed" });
            return;
        }

        const stopListening = () => {
            unsubSuccess();
            unsubError();
            clearTimeout(timer);
        };
        const finish = (reply: HighlightAssistReply, settled: Omit<HighlightAssistSettled, "durationMs">) => {
            if (isDone) return;
            isDone = true;
            stopListening();
            onReply(reply);
            settle(settled);
        };

        const unsubSuccess = gaiaWsEventBus.on(
            "gaia_ws_orchestrator-invoke-success",
            (response: GaiaWebSocketResponse) => {
                if (response.correlationId !== correlationId) return;
                const { formattedMessage, analytics } = parseExpertAssistMessage(response.body as GaiaBotMessage);
                const replyText = formattedMessage?.text?.trim();
                const replyInfo = { replyMessageId: formattedMessage?.id, replyAnalytics: analytics };
                if (replyText) finish({ status: "answered", text: replyText }, { outcome: "answered", ...replyInfo });
                else finish({ status: "failed" }, { outcome: "empty", ...replyInfo });
            },
        );
        const unsubError = gaiaWsEventBus.on("gaia_ws_orchestrator-invoke-error", (response: GaiaWebSocketResponse) => {
            if (response.correlationId === correlationId) finish({ status: "failed" }, { outcome: "error" });
        });
        const timer = setTimeout(() => finish({ status: "failed" }, { outcome: "timeout" }), REPLY_TIMEOUT_MS);

        stop = () => {
            stopListening();
            settle({ outcome: "closed" });
        };
    });

    return () => {
        if (isDone) return;
        isDone = true;
        stop?.();
    };
}
